// ============================================================================
// NPSDVehicleScreen.tsx - Main orchestrating screen for the NPSD Vehicle entity
// Converted from Vue 2 Vue-NPSDVehicle.ts (1181 lines)
//
// Manages tabs, edit/save lifecycle, connections, attachments, warnings,
// profile pic, and coordinates all child components.
// ============================================================================
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useNPSDEntity } from '@/hooks/useNPSDEntity';
import { NPSDTabBar } from '@/components/npsd/NPSDTabBar';
import { NPSDWarningsDisplay } from '@/components/npsd/NPSDWarningsDisplay';
import { NPSDVehicleDetails } from '@/components/npsd/NPSDVehicleDetails';
import { NPSDVehicleConnections } from '@/components/npsd/NPSDVehicleConnections';
import { NPSDVehicleHistory } from '@/components/npsd/NPSDVehicleHistory';
import { NPSDAttachments } from '@/components/npsd/NPSDAttachments';
import { NPSDWarnings } from '@/components/npsd/NPSDWarnings';
import { NPSDAuditLog } from '@/components/npsd/NPSDAuditLog';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import {
  refreshPage,
  refreshWithParams,
  navigateToRecord,
} from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { currentUserHasPermission, getModuleType, hasValue, hasStringValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { Codes } from '@/constants/codes';

// ── Global declarations (lazy lookups to avoid capturing before registration) ──
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
import { RelType } from '@/models/other-entities';
import { getAuthObject } from '@/utils/auth';
const isBasePageCaseReport = _wg('isBasePageCaseReport');
const isBasePageCallCard = _wg('isBasePageCallCard');
const isBasePageCitation = _wg('isBasePageCitation');
const setNPSDItemLocked = _wg('setNPSDItemLocked');
const updateNPSDItemForReport = _wg('updateNPSDItemForReport');
const updateNPSDItemForCallCard = _wg('updateNPSDItemForCallCard');
const saveAddNPSDData = _wg('saveAddNPSDData');
let currentVehicle = (window as any).currentVehicle;

// ── Props interface ─────────────────────────────────────────────────────
interface NPSDVehicleScreenProps {
  vehicleId?: number | string | null;
  isWindow?: boolean;
  isUnknown?: boolean;
  startInEditMode?: boolean;
  onClose?: () => void;
  onSaved?: (result: any) => void;
  onUpdated?: (result: any) => void;
  onAddedToReport?: (vehicle: any) => void;
  onAddedToConnection?: (vehicle: any) => void;
}

// ── Component ───────────────────────────────────────────────────────────
export function NPSDVehicleScreen({
  vehicleId: vehicleIdProp = null,
  isWindow = false,
  isUnknown = false,
  startInEditMode = false,
  onClose,
  onSaved,
  onUpdated,
  onAddedToReport,
  onAddedToConnection,
}: NPSDVehicleScreenProps) {
  const { theme } = useTheme();

  // ── Core state ──────────────────────────────────────────────────────
  const [vehicle, setVehicle] = useState<any>({
    id: null,
    make: '',
    model: '',
    year: '',
    color: '',
    plateState: '',
    plateNumber: '',
    plateCountry: '',
    temporaryPlate: '',
    plateIssued: null,
    plateExpires: null,
    insuranceCompanyName: '',
    insurancePolicyNumber: '',
    vin: '',
    bodyStyle: '',
    vehicleType: null,
    notes: '',
    descriptionPicId: null,
    descriptionPicInfoId: null,
    attachmentList: [],
    attachmentInfoList: [],
    warnings: [],
    connections: { people: [], businesses: [], clients: [], reports: [] },
    connectionsNew: { people: [], businesses: [], clients: [] },
    createdByCompanyId: null,
    createdByCompanyName: '',
  });
  const [originalVehicle, setOriginalVehicle] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('vehicle');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [isCaseReportMode, setIsCaseReportMode] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ── Mobile detection ────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);

  // ── Code lists ──────────────────────────────────────────────────────
  const [states, setStates] = useState<any[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);

  // ── Delete warning confirm ──────────────────────────────────────────
  const [deleteWarningConfirm, setDeleteWarningConfirm] = useState<{
    open: boolean;
    index: number | null;
  }>({ open: false, index: null });

  // ── useNPSDEntity hook ──────────────────────────────────────────────
  const {
    showConnectionSearchModal, connectionSearchType, openConnectionSearch, closeConnectionSearchModal,
    showViewPersonModal, viewPersonId, setShowViewPersonModal,
    showViewBusinessModal, viewBusinessId, setShowViewBusinessModal,
    showViewClientModal, viewClientId, setShowViewClientModal,
    openViewPersonModal, openViewBusinessModal, openViewClientModal,
    handleConnectionView,
    ensureConnectionsNewPopulated,
    handleAttachmentUpload,
    removeAttachment,
  } = useNPSDEntity({
    entityType: 'vehicle',
    setEntity: setVehicle,
    connectionTypes: ['people', 'businesses', 'clients'],
  });

  // ── Refs ────────────────────────────────────────────────────────────
  const profilePicInputRef = useRef<HTMLInputElement>(null);
  const attachmentsFileInputRef = useRef<HTMLInputElement>(null);
  const vehicleRef = useRef<any>(null);
  const isLockedRef = useRef(false);

  // Keep refs in sync
  vehicleRef.current = vehicle;
  isLockedRef.current = isLocked;

  // ── Computed values ─────────────────────────────────────────────────
  const canEdit = useMemo(() => {
    if (!vehicle.id) return false;
    if (isEditMode) return false;
    if (typeof getAuthObject === 'undefined' || typeof AccessAuthorities === 'undefined') return false;
    const authObj = getAuthObject();
    if (authObj.company_id !== vehicle.createdByCompanyId) return false;
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE);
  }, [vehicle.id, vehicle.createdByCompanyId, isEditMode]);

  const canUpdatePerm = useMemo(() => {
    if (typeof AccessAuthorities === 'undefined') return false;
    return vehicle.id && isEditMode && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE);
  }, [vehicle.id, isEditMode]);

  const canCancel = useMemo(() => canUpdatePerm, [canUpdatePerm]);

  const canSave = useMemo(() => {
    if (typeof AccessAuthorities === 'undefined') return false;
    return !vehicle.id && !isCaseReportMode && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, [vehicle.id, isCaseReportMode]);

  const canSaveToReport = useMemo(() => {
    if (typeof AccessAuthorities === 'undefined') return false;
    return !vehicle.id && isCaseReportMode && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, [vehicle.id, isCaseReportMode]);

  const enableFormField = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const showAddConnectionButton = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const connectionSearchTypeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      'person': 'Person',
      'business': 'Business',
      'client': 'Client',
    };
    return labels[connectionSearchType] || '';
  }, [connectionSearchType]);

  const showAddAttachmentButton = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const canEditProfilePic = useMemo(() => {
    if (typeof AccessAuthorities === 'undefined') return false;
    return (isEditMode && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE)) ||
           (isSaveMode && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE));
  }, [isEditMode, isSaveMode]);

  const vehicleTitle = useMemo(() => {
    const parts = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'New Vehicle';
  }, [vehicle.year, vehicle.make, vehicle.model]);

  const plateDisplay = useMemo(() => {
    const parts = [vehicle.plateCountry, vehicle.plateState, vehicle.plateNumber].filter(Boolean);
    return parts.join(' ') || 'No Plate';
  }, [vehicle.plateCountry, vehicle.plateState, vehicle.plateNumber]);

  const profilePicUrl = useMemo(() => {
    const attachmentId = vehicle.descriptionPicId;
    const attachment = vehicle.attachmentList.find((a: any) => a.id === attachmentId);
    if (!attachmentId || !attachment) {
      return '/Content/Images/defaultVehicle.png';
    }
    return attachment.signedURL;
  }, [vehicle.descriptionPicId, vehicle.attachmentList]);

  const connectionsCount = useMemo(() => {
    if (!vehicle?.connections) return 0;
    const c = vehicle.connections;
    return (c.people?.length || 0) + (c.businesses?.length || 0) + (c.clients?.length || 0);
  }, [vehicle?.connections]);

  const tabsConfig = useMemo(() => [
    { id: 'vehicle', label: 'Vehicle' },
    { id: 'connections', label: 'Connections', badge: connectionsCount },
    { id: 'history', label: 'History' },
    { id: 'attachments', label: 'Attachments', badge: vehicle?.attachmentList?.length || 0 },
    { id: 'warnings', label: 'Warnings', badge: vehicle?.warnings?.length || 0 },
    { id: 'auditlog', label: 'Audit Log' },
  ], [connectionsCount, vehicle?.attachmentList?.length, vehicle?.warnings?.length]);

  // ── Helper functions ────────────────────────────────────────────────

  const updateGlobalCurrentVehicle = useCallback((vehicleData: any) => {
    if (isWindow) return;
    currentVehicle = vehicleData;
    (window as any).currentVehicle = vehicleData;
    if (typeof App !== 'undefined') {
      App.originalVehicleResult = JSON.parse(JSON.stringify(vehicleData));
    }
  }, [isWindow]);

  const setVehicleFromResult = useCallback((result: any) => {
    const attachmentList = result.attachmentList || [];
    const attachmentInfoList: any[] = [];
    for (let i = 0; i < attachmentList.length; i++) {
      attachmentInfoList.push({
        id: attachmentList[i].id,
        activationKey: '',
      });
    }

    return {
      id: result.id,
      make: result.make || '',
      model: result.model || '',
      year: result.year || '',
      color: result.color || '',
      plateState: result.plateState || '',
      plateNumber: result.plateNumber || '',
      plateCountry: result.plateCountry || '',
      temporaryPlate: result.temporaryPlate || '',
      plateIssued: result.plateIssued ? new Date(result.plateIssued) : null,
      plateExpires: result.plateExpires ? new Date(result.plateExpires) : null,
      insuranceCompanyName: result.insuranceCompanyName || '',
      insurancePolicyNumber: result.insurancePolicyNumber || '',
      vin: result.vin || '',
      bodyStyle: result.bodyStyle || '',
      vehicleType: result.vehicleType,
      notes: result.notes || '',
      descriptionPicId: result.descriptionPicId,
      descriptionPicInfoId: result.descriptionPicId,
      attachmentList,
      attachmentInfoList,
      warnings: result.warnings || [],
      connections: {
        people: result.connections?.people || [],
        businesses: result.connections?.businesses || [],
        clients: result.connections?.clients || [],
        reports: result.connections?.reports || [],
      },
      connectionsOld: {
        people: JSON.parse(JSON.stringify(result.connections?.people || [])),
        businesses: JSON.parse(JSON.stringify(result.connections?.businesses || [])),
        clients: JSON.parse(JSON.stringify(result.connections?.clients || [])),
      },
      connectionsNew: {
        people: [],
        businesses: [],
        clients: [],
      },
      createdByCompanyId: result.createdByCompanyId,
      createdByCompanyName: result.createdByCompanyName || '',
    };
  }, []);

  const initNewVehicle = useCallback(() => {
    const newVehicle = {
      id: null,
      make: isUnknown ? 'Unknown' : '',
      model: isUnknown ? 'Unknown' : '',
      year: '',
      color: '',
      plateState: '',
      plateNumber: isUnknown ? 'Unknown' : '',
      plateCountry: '',
      temporaryPlate: '',
      plateIssued: null,
      plateExpires: null,
      insuranceCompanyName: '',
      insurancePolicyNumber: '',
      vin: '',
      bodyStyle: '',
      vehicleType: null,
      notes: '',
      descriptionPicId: null,
      descriptionPicInfoId: null,
      attachmentList: [] as any[],
      attachmentInfoList: [] as any[],
      warnings: [] as any[],
      connections: {
        people: [] as any[],
        businesses: [] as any[],
        clients: [] as any[],
        reports: [] as any[],
      },
      connectionsNew: {
        people: [] as any[],
        businesses: [] as any[],
        clients: [] as any[],
      },
      createdByCompanyId: null as number | null,
      createdByCompanyName: '',
    };

    setVehicle(newVehicle);
    setOriginalVehicle(JSON.parse(JSON.stringify(newVehicle)));
    updateGlobalCurrentVehicle(newVehicle);

    if (isWindow) {
      setIsCaseReportMode(true);
    }
    setIsSaveMode(true);
  }, [isUnknown, isWindow, updateGlobalCurrentVehicle]);

  const loadVehicle = useCallback((id: string | number) => {
    setIsLoading(true);
    api.getNPSDItemById(id.toString(), 'vehicle', (result: any) => {
      const transformed = setVehicleFromResult(result);
      setVehicle(transformed);
      setOriginalVehicle(JSON.parse(JSON.stringify(transformed)));
      setIsLoading(false);

      updateGlobalCurrentVehicle(transformed);

      if (startInEditMode) {
        // Lock and enter edit mode
        if (transformed.id) {
          setNPSDItemLocked(transformed.id.toString(), true, 'vehicle', () => {
            setIsLocked(true);
            setIsEditMode(true);
          });
        }
      }
    });
  }, [setVehicleFromResult, updateGlobalCurrentVehicle, startInEditMode]);

  const loadCodes = useCallback(() => {
    if (typeof Codes !== 'undefined') {
      setStates(Codes.getStates() || []);
      setVehicleTypes(Codes.getVehicleTypes() || []);
    }
  }, []);

  // ── Mount / Unmount ─────────────────────────────────────────────────
  useEffect(() => {
    // Mobile check (debounced to avoid excessive re-renders during resize)
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    loadCodes();
    window.addEventListener('resize', checkIfMobile);

    if (hasValue(vehicleIdProp)) {
      loadVehicle(vehicleIdProp!);
    } else {
      initNewVehicle();
    }

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (mobileTimer) clearTimeout(mobileTimer);

      // Unlock vehicle if we had it locked
      if (isLockedRef.current && vehicleRef.current?.id) {
        setNPSDItemLocked(vehicleRef.current.id.toString(), false, 'vehicle', () => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watch vehicleId prop changes ────────────────────────────────────
  const prevVehicleIdRef = useRef(vehicleIdProp);
  useEffect(() => {
    if (prevVehicleIdRef.current !== vehicleIdProp) {
      prevVehicleIdRef.current = vehicleIdProp;
      if (hasValue(vehicleIdProp)) {
        loadVehicle(vehicleIdProp!);
      } else {
        initNewVehicle();
      }
    }
  }, [vehicleIdProp, loadVehicle, initNewVehicle]);

  // ── Keep global currentVehicle in sync ──────────────────────────────
  useEffect(() => {
    if (!isWindow && vehicle) {
      currentVehicle = vehicle;
      (window as any).currentVehicle = vehicle;
    }
  }, [vehicle, isWindow]);

  // ── Edit/Save/Cancel lifecycle ──────────────────────────────────────

  const lockVehicle = useCallback(() => {
    if (vehicle.id) {
      setNPSDItemLocked(vehicle.id.toString(), true, 'vehicle', () => {
        setIsLocked(true);
      });
    }
  }, [vehicle.id]);

  const unlockVehicle = useCallback(() => {
    if (vehicle.id && isLocked) {
      setNPSDItemLocked(vehicle.id.toString(), false, 'vehicle', () => {
        setIsLocked(false);
      });
    }
  }, [vehicle.id, isLocked]);

  const edit = useCallback(() => {
    setIsEditMode(true);
    lockVehicle();
  }, [lockVehicle]);

  const cancel = useCallback(() => {
    if (isWindow) {
      onClose?.();
      unlockVehicle();
      return;
    }

    setIsEditMode(false);
    unlockVehicle();

    // Check if there are unsaved changes
    const hasChanges = JSON.stringify(vehicle) !== JSON.stringify(originalVehicle);
    if (hasChanges && !isWindow) {
      setOriginalVehicle(null);
      refreshPage();
    }
  }, [isWindow, vehicle, originalVehicle, onClose, unlockVehicle]);

  const getVehicleForSave = useCallback((v: any): any => {
    return {
      id: v.id,
      make: v.make,
      model: v.model,
      year: v.year,
      color: v.color,
      plateState: v.plateState,
      plateNumber: v.plateNumber,
      plateCountry: v.plateCountry,
      temporaryPlate: v.temporaryPlate,
      plateIssued: v.plateIssued,
      plateExpires: v.plateExpires,
      insuranceCompanyName: v.insuranceCompanyName,
      insurancePolicyNumber: v.insurancePolicyNumber,
      vin: v.vin,
      bodyStyle: v.bodyStyle,
      vehicleType: v.vehicleType,
      notes: v.notes,
      descriptionPicId: v.descriptionPicId,
      descriptionPicInfoId: v.descriptionPicInfoId,
      attachmentList: v.attachmentList,
      attachmentInfoList: v.attachmentInfoList,
      warnings: v.warnings,
      connections: v.connections,
      connectionsOld: v.connectionsOld,
      connectionsNew: v.connectionsNew,
    };
  }, []);

  const saveOrUpdateVehicle = useCallback(() => {
    const currentVeh = vehicleRef.current;
    setIsEditMode(false);

    const withConnections = ensureConnectionsNewPopulated(currentVeh);
    const vehicleData = getVehicleForSave(withConnections);

    if (!currentVeh.id) {
      // Save new vehicle
      api.saveNPSDItem(vehicleData, 'vehicle', (result: any) => {
        setOriginalVehicle(null);
        if (typeof App !== 'undefined') App.originalVehicleResult = null;
        if (!isWindow && !isBasePageCaseReport()) {
          refreshWithParams(result.id);
        }
        onSaved?.(result);
      });
    } else {
      // Update attachment titles if changed
      for (const item of currentVeh.attachmentList) {
        const origItem = originalVehicle?.attachmentList?.find((d: any) => d.id === item.id);
        if ((origItem && item.title !== origItem.title) || !origItem) {
          api.updateAttachment(item.title, item.id, () => {});
        }
      }

      // Update existing vehicle
      api.updateNPSDItem(vehicleData, 'vehicle', (result: any) => {
        // Handle case report context
        const rptVue = (window as any).CaseRpt?.currentMDTRptVue as any;
        if (typeof (window as any).CaseRpt !== 'undefined' && rptVue?.$data?.showViewVehicleModal) {
          rptVue.closeViewVehicleModal();
        } else if (rptVue?.$data?.showInvolvedSearchModal) {
          rptVue.closeInvolvedSearchModal();
        }

        setOriginalVehicle(null);
        if (typeof App !== 'undefined') App.originalVehicleResult = null;

        if (!isWindow && !isBasePageCaseReport() && !isBasePageCallCard() && !isBasePageCitation()) {
          refreshPage();
        }

        // Update call card if applicable
        if (isBasePageCallCard()) {
          if (App.callCardController?.showVehicleModal) {
            const uuid = App.callCardController.vehicleModalUuid;
            if (uuid) {
              updateNPSDItemForCallCard(RelType.vehicle, vehicleData, uuid);
            }
            App.callCardController.closeVehicleModal();
          }
        }

        // Update case report if applicable
        if (isBasePageCaseReport()) {
          updateNPSDItemForReport(RelType.vehicle, vehicleData);
        }

        if (isWindow) {
          onUpdated?.(result);
          onClose?.();
        } else {
          onUpdated?.(result);
        }
      }, (error: any) => {
        setIsEditMode(true);
        App?.showPopupNotification('Error updating vehicle', 'error');
      });
    }
  }, [ensureConnectionsNewPopulated, getVehicleForSave, isWindow, originalVehicle, onSaved, onUpdated, onClose]);

  const saveToReport = useCallback(() => {
    saveAddNPSDData({
      thisJQuery: null,
      locationField: null,
      currentData: getVehicleForSave(vehicleRef.current),
      npsdType: 'vehicle',
      npsdItemToReportId: 'lnkRptAddVehicle',
      verifyAddress: false,
    });
  }, [getVehicleForSave]);

  const addToReport = useCallback(() => {
    const addToReportFn = (window as any).NPSDItemToReport;
    if (typeof addToReportFn === 'function') addToReportFn('lnkRptAddVehicle', vehicle.id);
    onAddedToReport?.(vehicle);
  }, [vehicle, onAddedToReport]);

  const addToConnection = useCallback(() => {
    onAddedToConnection?.(vehicle);
  }, [vehicle, onAddedToConnection]);

  const linkToCallCard = useCallback(() => {
    const cc = App.callCardController as any;
    if (cc?.showConnectionsSearchModal && cc?.connectionsSearchCallback) {
      cc.connectionsSearchCallback('lnkCallCardLinkVehicle', vehicle);
      cc.closeConnectionsSearchModal();
    }
  }, [vehicle]);

  // ── Vehicle update handler (from child details component) ───────────
  const handleVehicleChange = useCallback((updated: any) => {
    setVehicle(updated);
  }, []);

  // ── Warning methods ─────────────────────────────────────────────────
  const handleWarningsChange = useCallback((updatedWarnings: any[]) => {
    setVehicle((prev: any) => {
      if (!prev) return prev;
      return { ...prev, warnings: updatedWarnings };
    });
  }, []);

  const confirmRemoveWarning = useCallback((index: number) => {
    setDeleteWarningConfirm({ open: true, index });
  }, []);

  const removeWarning = useCallback(() => {
    if (deleteWarningConfirm.index !== null) {
      setVehicle((prev: any) => {
        if (!prev) return prev;
        const updatedWarnings = [...prev.warnings];
        updatedWarnings.splice(deleteWarningConfirm.index!, 1);
        return { ...prev, warnings: updatedWarnings };
      });
    }
    setDeleteWarningConfirm({ open: false, index: null });
  }, [deleteWarningConfirm.index]);

  const cancelRemoveWarning = useCallback(() => {
    setDeleteWarningConfirm({ open: false, index: null });
  }, []);

  // ── Attachment methods ──────────────────────────────────────────────
  const addAttachment = useCallback(() => {
    if (!isSaveMode && !isEditMode) {
      edit();
    }
    if (attachmentsFileInputRef.current) {
      attachmentsFileInputRef.current.click();
    }
  }, [isSaveMode, isEditMode, edit]);

  const setProfilePic = useCallback((attachmentId: number) => {
    setVehicle((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        descriptionPicId: attachmentId,
        descriptionPicInfoId: attachmentId,
      };
    });
    const saveText = vehicle.id ? 'Update' : 'Save';
    App?.showPopupNotification(`Picture set. Please click ${saveText} at the top to save changes.`, 'success');
  }, [vehicle.id]);

  const editProfilePic = useCallback(() => {
    if (!canEditProfilePic) return;
    if (!isSaveMode && !isEditMode) {
      edit();
    }
    if (profilePicInputRef.current) {
      profilePicInputRef.current.click();
    }
  }, [canEditProfilePic, isSaveMode, isEditMode, edit]);

  const handleProfilePicUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;

    if (typeof App !== 'undefined' && App.uploadFromFileInput) {
      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (idx: number, attachmentInfo: any) => {
          setVehicle((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentInfoList: [...prev.attachmentInfoList, attachmentInfo],
            };
          });
        },
        updateAttachment: (idx: number, attachment: any) => {
          setVehicle((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentList: [...prev.attachmentList, attachment],
            };
          });
        },
        successCallback: (idx: number, attachment: any) => {
          setVehicle((prev: any) => {
            if (!prev) return prev;
            const updatedList = prev.attachmentList.map((a: any) => {
              if (a.id === attachment.id) {
                return { ...a, signedURL: attachment.signedURL + '#' + new Date().getTime() };
              }
              return a;
            });
            return {
              ...prev,
              attachmentList: updatedList,
              descriptionPicId: attachment.id,
              descriptionPicInfoId: attachment.id,
            };
          });

          const saveText = vehicleRef.current?.id ? 'Update' : 'Save';
          App?.showPopupNotification(`Picture set. Please click ${saveText} at the top to save changes.`, 'success');
        },
      });
    }
  }, []);

  // ── Connection methods ──────────────────────────────────────────────
  const handleConnectionRemoved = useCallback((connectionType: string, itemId: number) => {
    setVehicle((prev: any) => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      const pluralType = connectionType === 'person' ? 'people' :
                         connectionType === 'business' ? 'businesses' :
                         connectionType === 'client' ? 'clients' :
                         connectionType + 's';

      if (updated.connectionsNew?.[pluralType]) {
        updated.connectionsNew[pluralType] = updated.connectionsNew[pluralType].filter((item: any) => item.id !== itemId);
      }
      if (updated.connections?.[pluralType]) {
        updated.connections[pluralType] = updated.connections[pluralType].filter((item: any) => item.id !== itemId);
      }
      return updated;
    });
  }, []);

  const addConnectionToEntity = useCallback((type: string, item: any) => {
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    let connection: any = { id: item.id };

    if (type === 'person') {
      connection = {
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        dob: item.dob,
      };
    } else if (type === 'business') {
      connection = {
        id: item.id,
        businessName: item.businessName || item.name,
        branchName: item.branchName,
        website: item.website,
      };
    } else if (type === 'client') {
      connection = {
        id: item.id,
        name: item.name,
        locCity: item.locCity,
        locState: item.locState,
        website: item.website,
      };
    }

    setVehicle((prev: any) => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.connections[pluralType]) updated.connections[pluralType] = [];
      if (!updated.connectionsNew[pluralType]) updated.connectionsNew[pluralType] = [];
      updated.connections[pluralType].push(connection);
      updated.connectionsNew[pluralType].push(connection);
      return updated;
    });

    App?.showPopupNotification('Connection added.', 'success');
    closeConnectionSearchModal();
  }, [closeConnectionSearchModal]);

  const handleConnectionSelect = useCallback((item: any) => {
    const type = connectionSearchType;
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    const existingArray = vehicle.connections?.[pluralType] || [];
    const isDuplicate = existingArray.some((c: any) => c.id === item.id);

    if (isDuplicate) {
      App?.showPopupNotification('This connection already exists.', 'warning');
      return;
    }

    // Vehicle connections don't need relationship dialog
    addConnectionToEntity(type, item);
  }, [connectionSearchType, vehicle, addConnectionToEntity]);

  // ── View NPSD item - open in PageModal ──────────────────────────────
  const viewNpsdItem = useCallback((type: string, item: any) => {
    const id = typeof item === 'object' ? item.id : item;
    switch (type) {
      case 'person': openViewPersonModal(id); break;
      case 'business': openViewBusinessModal(id); break;
      case 'client': openViewClientModal(id); break;
    }
  }, [openViewPersonModal, openViewBusinessModal, openViewClientModal]);


  // ── View modal close handlers ───────────────────────────────────────
  const closeViewPersonModal = useCallback(() => {
    setShowViewPersonModal(false);
  }, [setShowViewPersonModal]);

  const closeViewBusinessModal = useCallback(() => {
    setShowViewBusinessModal(false);
  }, [setShowViewBusinessModal]);

  const closeViewClientModal = useCallback(() => {
    setShowViewClientModal(false);
  }, [setShowViewClientModal]);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className={`npsd-entity-screen npsd-vehicle-screen ${theme}`}>
      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="vehicle-content">
          {/* Header Section */}
          <div className="profile-card-header">
            <div className="profile-cover-details">
              <div className="profile-image-container">
                <a
                  className={`profile-image${!canEditProfilePic ? ' vue-disabled' : ''}`}
                  onClick={editProfilePic}
                >
                  <img src={profilePicUrl} alt="Vehicle" />
                </a>
              </div>
              <div className="profile-info">
                <h3 className="vehicle-title">
                  {plateDisplay}<br />
                  {vehicleTitle}
                </h3>
              </div>
            </div>

            <div className="action-buttons">
              {canEdit && (
                <button className="btn btn-header" onClick={edit}>Edit</button>
              )}
              {vehicle.createdByCompanyName && vehicle.createdByCompanyId !== getAuthObject().company_id && (
                <span className="created-by">{vehicle.createdByCompanyName}</span>
              )}
              {canUpdatePerm && (
                <button className="kstc-btn kstc-btn--success" onClick={saveOrUpdateVehicle}>Update</button>
              )}
              {canCancel && (
                <button className="kstc-btn kstc-btn--danger" onClick={cancel}>Cancel</button>
              )}
              {canSave && (
                <button className="kstc-btn kstc-btn--success" onClick={saveOrUpdateVehicle}>Save</button>
              )}
              {canSaveToReport && (
                <button className="kstc-btn kstc-btn--success" onClick={saveToReport}>Save/Add</button>
              )}
            </div>
            <NPSDWarningsDisplay warnings={vehicle.warnings || []} />
          </div>

          {/* Tab Navigation */}
          <NPSDTabBar
            tabs={tabsConfig}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="vehicle-tab-content">
            {/* Vehicle Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'vehicle' ? undefined : 'none' }}>
              <NPSDVehicleDetails
                vehicle={vehicle}
                isReadOnly={!enableFormField}
                states={states}
                vehicleTypes={vehicleTypes}
                onChange={handleVehicleChange}
              />
            </div>

            {/* Connections Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'connections' ? undefined : 'none' }}>
              <NPSDVehicleConnections
                people={vehicle.connections.people}
                businesses={vehicle.connections.businesses}
                clients={vehicle.connections.clients}
                isReadOnly={!showAddConnectionButton}
                vehicleId={vehicle.id}
                onAddConnection={openConnectionSearch}
                onRemoveConnection={handleConnectionRemoved}
                onViewConnection={viewNpsdItem}
              />
            </div>

            {/* History Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'history' ? undefined : 'none' }}>
              <NPSDVehicleHistory
                vehicleId={vehicle.id}
                reports={vehicle.connections.reports}
              />
            </div>

            {/* Attachments Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'attachments' ? undefined : 'none' }}>
              <NPSDAttachments
                attachmentList={vehicle.attachmentList}
                isEditMode={showAddAttachmentButton}
                theme={theme}
                showProfilePicOption={true}
                profilePicId={vehicle.descriptionPicId}
                onAdd={addAttachment}
                onRemove={removeAttachment}
                onSetProfilePic={setProfilePic}
              />
            </div>

            {/* Warnings Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'warnings' ? undefined : 'none' }}>
              <NPSDWarnings
                warnings={vehicle.warnings}
                isReadOnly={!enableFormField}
                title="Warnings"
                onChange={handleWarningsChange}
              />
            </div>

            {/* Audit Log Tab */}
            <div className="vehicle-tab-panel" style={{ display: activeTab === 'auditlog' ? undefined : 'none' }}>
              <NPSDAuditLog
                entityId={vehicle.id}
                entityType="vehicle"
                title="Audit Log"
              />
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={profilePicInputRef}
        onChange={handleProfilePicUpload}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={attachmentsFileInputRef}
        onChange={handleAttachmentUpload}
        multiple
        style={{ display: 'none' }}
      />

      {/* Confirm Modal for Warning Deletion */}
      <ConfirmModal
        open={deleteWarningConfirm.open}
        title="Remove Warning?"
        messageHtml="Are you sure you want to remove this warning?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        onConfirm={removeWarning}
        onCancel={cancelRemoveWarning}
      />

      {/* Connection Search Modal */}
      <SearchModal
        open={showConnectionSearchModal}
        searchType={connectionSearchType as any}
        title={'Add ' + connectionSearchTypeLabel + ' Connection'}
        theme={theme}
        onSelect={handleConnectionSelect}
        onView={handleConnectionView}
        onCancel={closeConnectionSearchModal}
      />

      {/* View Person Modal */}
      <PageModal
        open={showViewPersonModal}
        title="View Person"
        modalId="vehicleViewPerson"
        onClose={closeViewPersonModal}
      >
        {showViewPersonModal && viewPersonId && (
          <NPSDPersonScreen
            personId={viewPersonId}
            isWindow={true}
            onClose={closeViewPersonModal}
          />
        )}
      </PageModal>

      {/* View Business Modal */}
      <PageModal
        open={showViewBusinessModal}
        title="View Business"
        modalId="vehicleViewBusiness"
        onClose={closeViewBusinessModal}
      >
        {showViewBusinessModal && viewBusinessId && (
          <NPSDBusinessScreen
            businessId={viewBusinessId}
            isModal={true}
            onClose={closeViewBusinessModal}
          />
        )}
      </PageModal>

      {/* View Client Modal */}
      <PageModal
        open={showViewClientModal}
        title="View Client"
        modalId="vehicleViewClient"
        onClose={closeViewClientModal}
      >
        {showViewClientModal && viewClientId && (
          <NPSDClientScreenWrapper
            clientId={String(viewClientId)}
          />
        )}
      </PageModal>
    </div>
  );
}

// ── Screen registry ──────────────────────────────────────────────────────
